"use client";

import useSWR, { preload } from "swr";
import { useAuth } from "@clerk/nextjs";
import { useEffect, useMemo } from "react";
import { swrFetcher } from "@/components/providers/swr-provider";

// Persist freshness across unmounts per user to avoid refetching on quick tab flips
const profileFreshness = new Map<string, number>();
const PROFILE_CACHE_KEY = "/api/dashboard/profile";
const settingsFreshness = new Map<string, number>();
const SETTINGS_CACHE_KEY = "/api/dashboard/settings";

/**
 * Hook to fetch all Profile page data in one request
 * REBUILT FROM SCRATCH - Simple, clean approach
 */
export function useProfileData(fallbackData?: unknown) {
  const { isLoaded, userId } = useAuth();

  // Allow fetch only when auth is ready
  const shouldFetch = isLoaded && userId;

  // Treat data as fresh for 30s per user to avoid refetch on rapid tab switches
  const isFresh = useMemo(() => {
    const lastResolved = userId ? profileFreshness.get(userId) : null;
    return lastResolved != null && Date.now() - lastResolved < 30_000;
  }, [userId]);

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    shouldFetch ? PROFILE_CACHE_KEY : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      // If we already have data and it's fresh, skip revalidate on mount/stale
      revalidateOnMount: !isFresh,
      revalidateIfStale: !isFresh,
      fallbackData,
    },
  );

  // Update freshness marker whenever we have data
  useEffect(() => {
    if (userId && data?.data) {
      profileFreshness.set(userId, Date.now());
    }
  }, [data, userId]);

  return {
    account: data?.data?.account || null,
    stats: data?.data?.stats,
    domains: data?.data?.domains || [],
    recentActivity: data?.data?.recentActivity || [],
    earnedBadges: data?.data?.earnedBadges || [],
    allBadges: data?.data?.allBadges || [],
    badgesByRarity: data?.data?.badgesByRarity,
    badgeStats: data?.data?.badgeStats,
    isLoading: !shouldFetch || (isLoading && !data && !fallbackData),
    isValidating,
    error: error?.message || null,
    mutate,
  };
}

/**
 * Hook to fetch all Settings page data in one request
 * REBUILT FROM SCRATCH - Simple, clean approach
 */
export function useSettingsData(fallbackData?: unknown) {
  const { isLoaded, userId } = useAuth();

  // Simple approach: only fetch if auth is ready
  const shouldFetch = isLoaded && userId;

  // Treat data as fresh for 30s per user to avoid refetch on rapid tab switches
  const isFresh = useMemo(() => {
    const lastResolved = userId ? settingsFreshness.get(userId) : null;
    return lastResolved != null && Date.now() - lastResolved < 30_000;
  }, [userId]);

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    shouldFetch ? SETTINGS_CACHE_KEY : null,
    {
      revalidateOnFocus: false,
      revalidateOnMount: !isFresh,
      revalidateOnReconnect: false,
      revalidateIfStale: !isFresh,
      fallbackData,
    },
  );

  useEffect(() => {
    if (userId && data?.data) {
      settingsFreshness.set(userId, Date.now());
    }
  }, [data, userId]);

  return {
    connections: data?.data?.connections || [],
    focusAreas: data?.data?.focusAreas || [],
    hasData: !!data?.data,
    isLoading: !shouldFetch || (isLoading && !data),
    isValidating,
    error: error?.message || null,
    mutate,
  };
}

/**
 * Prefetch profile data for instant navigation
 */
export function prefetchProfileData() {
  preload(PROFILE_CACHE_KEY, swrFetcher).catch(() => {});
}

/**
 * Prefetch settings data for instant navigation
 */
export function prefetchSettingsData() {
  preload(SETTINGS_CACHE_KEY, swrFetcher).catch(() => {});
}
